import type { TimeRange } from "@uri/time-utils";
import { z } from "zod/v4";
import type { Category } from "./category.ts";

export const submitSourceKindSchema = z.enum([
  "instagram-page",
  "whatsapp-group",
]);

export const sourceAttributedSubmitSchema = z.object({
  text: z.string(),
  categories: z.array(z.string()).optional(),
  links: z.array(z.string()).optional(),
  source: z.string(),
  sourceKind: submitSourceKindSchema.optional(),
  sourceRef: z.string().optional(),
});

export const storedEventSchema = z.object({
  categories: z.array(z.string()),
  country: z.string(),
  coordinates: z.any().optional(),
  city: z.string().optional(),
  coupon: z.string().optional(),
  description: z.string().optional(),
  performers: z.array(z.string()).optional(),
  links: z.array(z.string()),
  location: z.string().optional(),
  name: z.string(),
  priceRange: z.object({ start: z.number(), end: z.number() }).optional(),
  timestamp: z.object({ start: z.number(), end: z.number() }),
  venue: z.string().optional(),
  promotionLink: z.string().optional(),
  promotionExpiration: z.number().optional(),
  minAge: z.number().optional(),
  posterUrl: z.string().optional(),
  videoUrl: z.string().optional(),
});

export type StoredEvent = z.infer<typeof storedEventSchema>;

export const eventEntrySchema = z.object({
  structured: storedEventSchema,
  ingestionTime: z.number(),
  apiSubmit: z.union([z.any(), sourceAttributedSubmitSchema]).optional(),
  userSubmit: z.object({ user: z.string(), text: z.string() }).optional(),
  visitCount: z.number().optional(),
  seenCount: z.number().optional(),
  id: z.string(),
  recommendationScore: z.number().optional(),
});

export type EventEntry = z.infer<typeof eventEntrySchema>;

export type EventsQuery = {
  limit: number;
  skip: number;
  priceRange?: { start?: number; end?: number };
  location?: string;
  radiusKm?: number;
  country?: string;
  categories?: Category[];
  // Categories where ANY match (OR semantics)
  // When using new explicit intersectionCategories field, do not include those categories here.
  negativeCategories?: Category[];
  intersectionCategories?: Category[];
  timeRange?: TimeRange;
  // When true, only include events whose start time is within the range.
  // When false/undefined (default), include events that either start or end within the range (includes ongoing events that started before).
  onlyStartedInRange?: boolean;
  performers?: string[];
  venues?: string[];
  texts?: string[];
  excludeIds?: string[];
  coordinates?: [number, number];
  minAge?: number;
};

export const daysToDefault = (days: number | undefined) => days || 14;
